/**
 * Planta-baixa do escritório AGÊNCIA VPC.
 * Coordenadas em "unidades" lógicas (renderizadas com escala em SVG).
 * Grid base: 100 x 64.
 */

export interface Room {
  id: string;
  name: string;
  /** id do departamento ocupante (ou null se for sala comum) */
  deptId: string | null;
  /** rótulo curto pra header */
  code: string;
  /** se compartilha sala com outros depts (small dept fusion) */
  sharedWith?: string[];
  /** retângulo da sala em unidades */
  x: number; y: number; w: number; h: number;
  /** tipo visual */
  kind: "dept" | "council" | "management" | "common" | "meeting";
  /** ícone/emoji opcional */
  icon?: string;
}

export interface OfficeObject {
  id: string;
  label: string;
  icon: string;
  /** posição central */
  x: number; y: number;
  kind: "printer" | "coffee" | "water" | "fridge" | "plant" | "lounge" | "reception";
  hint?: string;
}

export const FLOOR = { w: 100, h: 100 };

export const ROOMS: Room[] = [
  // ========= TOPO: CONSELHO + GERÊNCIA =========
  { id: "council",     name: "Conselho", code: "C-00",
    deptId: "council", kind: "council",
    x: 4,  y: 2, w: 40, h: 12, icon: "♛" },

  { id: "management",  name: "Gerência Geral", code: "M-00",
    deptId: "management", kind: "management",
    x: 46, y: 2, w: 22, h: 12, icon: "▲" },

  { id: "meeting-room",name: "Sala de Reunião", code: "SR-01",
    deptId: null, kind: "meeting",
    x: 70, y: 2, w: 26, h: 12, icon: "▣" },

  // ========= CORREDOR (apenas implícito entre y=14 e y=16) =========

  // ========= MEIO ESQUERDO: COPY / CRIATIVO =========
  { id: "copy",        name: "Copy", code: "DPT-A",
    deptId: "copy", kind: "dept",
    x: 4,  y: 16, w: 30, h: 22, icon: "✎" },

  { id: "criativo",    name: "Criativo", code: "DPT-B",
    deptId: "criativo", kind: "dept",
    x: 36, y: 16, w: 30, h: 22, icon: "◈" },

  // ========= MEIO DIREITO: VIDEO + sala compartilhada (Jurídico/Financeiro) =========
  { id: "video",       name: "Vídeo", code: "DPT-C",
    deptId: "video", kind: "dept",
    x: 68, y: 16, w: 28, h: 14, icon: "▶" },

  { id: "shared-jf",   name: "Jurídico · Financeiro (compartilhada)", code: "SH-1",
    deptId: "juridico", sharedWith: ["financeiro"], kind: "dept",
    x: 68, y: 32, w: 28, h: 14, icon: "§" },

  // ========= BAIXO: TRAFEGO / VENDAS =========
  { id: "trafego",     name: "Tráfego", code: "DPT-D",
    deptId: "trafego", kind: "dept",
    x: 4,  y: 40, w: 30, h: 14, icon: "↗" },

  { id: "vendas",      name: "Vendas", code: "DPT-E",
    deptId: "vendas", kind: "dept",
    x: 36, y: 40, w: 30, h: 14, icon: "$" },

  // ========= PROGRAMAÇÃO + MÍDIAS SOCIAIS (faixa dividida) =========
  { id: "programacao", name: "Programação · Dev Lab", code: "DPT-I",
    deptId: "programacao", kind: "dept",
    x: 4, y: 56, w: 46, h: 14, icon: "⌬" },
  { id: "social", name: "Mídias Sociais · Studio", code: "DPT-J",
    deptId: "social", kind: "dept",
    x: 52, y: 56, w: 44, h: 14, icon: "✦" },

  // ========= PROMPT LAB (faixa intermediária) =========
  { id: "prompts", name: "Prompt Lab · IA Whisperers", code: "DPT-K",
    deptId: "prompts", kind: "dept",
    x: 4, y: 72, w: 92, h: 12, icon: "✦" },

  // ========= ÁREA COMUM (rodapé esquerda) =========
  { id: "lounge",      name: "Café · Lounge · Impressora", code: "AC-1",
    deptId: null, kind: "common",
    x: 4,  y: 86, w: 62, h: 12, icon: "☕" },

  // ========= ENDOMARKETING (rodapé direita) =========
  { id: "endomarketing", name: "Endomarketing · Cultura", code: "DPT-H",
    deptId: "endomarketing", kind: "dept",
    x: 68, y: 86, w: 28, h: 12, icon: "♪" }
];

export const OBJECTS: OfficeObject[] = [
  { id: "reception", label: "Recepção",       icon: "✿", x: 50, y: 1,   kind: "reception", hint: "porta de entrada" },
  { id: "coffee",    label: "Cafeteria",      icon: "☕", x: 12,  y: 92, kind: "coffee",  hint: "máquina de café — onde nasce a estratégia" },
  { id: "fridge",    label: "Geladeira",      icon: "❄", x: 22,  y: 92, kind: "fridge",  hint: "geladeira compartilhada" },
  { id: "water",     label: "Bebedouro",      icon: "≋", x: 32,  y: 92, kind: "water",   hint: "água — hidrate o time" },
  { id: "printer",   label: "Impressora",     icon: "▤", x: 42,  y: 92, kind: "printer", hint: "impressora multifuncional" },
  { id: "plant-1",   label: "Planta",         icon: "❦", x: 52,  y: 92, kind: "plant",   hint: "decoração biofílica" },
  { id: "lounge",    label: "Lounge",         icon: "▭", x: 60,  y: 92, kind: "lounge",  hint: "puffs + revistas — pausa estratégica" }
];

/** Quais departamentos vivem na sala "deptId"? (resolve sharedWith) */
export function deptsInRoom(room: Room): string[] {
  if (!room.deptId) return [];
  return [room.deptId, ...(room.sharedWith ?? [])];
}

/** Sala que contém o departamento informado. */
export function roomForDept(deptId: string): Room | undefined {
  return ROOMS.find(r => r.deptId === deptId || r.sharedWith?.includes(deptId));
}
